import React from 'react';
import { cn } from '@/lib/utils';
import { RealtimeCommunity } from '@/hooks/use-realtime-community-extraction';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Target, Hash, X } from 'lucide-react';

interface CommunityPreviewProps {
  communities: RealtimeCommunity[];
  isExtracting: boolean;
  className?: string;
  disabledCommunities?: Set<string>;
  onToggleCommunity?: (communityId: string) => void;
}

const CommunityPreview: React.FC<CommunityPreviewProps> = ({
  communities,
  isExtracting,
  className,
  disabledCommunities = new Set(),
  onToggleCommunity,
}) => {
  if (!communities.length && !isExtracting) {
    return null;
  }

  const getConfidenceStyles = (confidence: number) => {
    if (confidence >= 0.8) {
      return "bg-primary/20 text-primary border-primary/30 hover:bg-primary/30";
    } else if (confidence >= 0.6) {
      return "bg-secondary/20 text-secondary border-secondary/30 hover:bg-secondary/30";
    } else {
      return "bg-muted/20 text-muted-foreground border-muted/30 hover:bg-muted/30";
    }
  };

  const getSourceIcon = (source: string, isOfficial: boolean) => {
    if (isOfficial) return <Target className="w-3 h-3" />;
    if (source === 'hashtag') return <Hash className="w-3 h-3" />;
    return <Sparkles className="w-3 h-3" />;
  };

  return (
    <div className={cn("mt-2 space-y-2", className)}>
      {isExtracting && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          Detecting communities...
        </div>
      )}
      
      {communities.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            Predicted communities:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {communities.map((community) => {
              const isDisabled = disabledCommunities.has(community.id);
              
              return (
                <Badge
                  key={community.id}
                  variant="outline"
                  className={cn(
                    "text-xs py-1 px-2 transition-all duration-200 cursor-pointer group relative",
                    getConfidenceStyles(community.confidence),
                    isDisabled && "opacity-40 grayscale line-through"
                  )}
                  style={community.gradient && !isDisabled ? {
                    background: community.gradient,
                    color: 'white',
                    border: 'none'
                  } : undefined}
                  onClick={() => {
                    console.log('[CommunityPreview] Toggling community:', {
                      id: community.id,
                      communityName: community.communityName,
                      displayName: community.displayName,
                      isOfficial: community.isOfficial,
                      source: community.source,
                      willBeDisabled: !isDisabled
                    });
                    onToggleCommunity?.(community.id);
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    {getSourceIcon(community.source, community.isOfficial)}
                    <span>{community.displayName}</span>
                    {community.isOfficial && !isDisabled && (
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                    )}
                    {onToggleCommunity && (
                      <X className={cn(
                        "w-3 h-3 ml-0.5 transition-opacity",
                        isDisabled ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                      )} />
                    )}
                  </div>
                </Badge>
              );
            })}
          </div>
          
          {communities.length > 0 && (
            <div className="text-xs text-muted-foreground/70 mt-1">
              💡 {onToggleCommunity 
                ? "Click to disable/enable communities • Enabled ones will be added when you post"
                : "These will be added automatically when you post"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunityPreview;